import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { BufferBagsContext } from '.';
import { axios } from '../../api';
import { getBufferBags } from '../../api/getBufferBags';
import { BufferBag, BufferBagProduct } from '../../pages/types';
import {
  BOTTOM_TOAST,
  BufferBagSelectionKey,
  calculateTotalsPerDog,
  generateErrorMsg,
  generateFormattedBufferOptions,
  PetPlanBufferBagTotals,
  prepareBufferBagProducts,
  useOrderSummaryProductsForPayload,
} from '../../utils';
import { ConfirmationModal } from '../ConfirmationModal';
import { Dropdown } from '../Dropdown';
import { Button } from '../buttons/Button';

export const BufferBagsStep2 = () => {
  const [showBufferBagsConfirmationModal, setShowBufferBagsConfirmationModal] = useState(false);
  const [showFreeBufferBagsConfirmationModal, setShowFreeBufferBagsConfirmationModal] =
    useState(false);

  const [selectedBufferBags, setSelectedBufferBags] = useState<
    Record<BufferBagSelectionKey, number>
  >({});

  const [bufferBagList, setBufferBagList] = useState<BufferBag[]>([]);

  const [totals, setTotals] = useState<PetPlanBufferBagTotals>({
    totalDays: 0,
    totalCost: 0,
  });

  const { selectedDog, order, customer, onConfirm, scheduledDate } = useContext(BufferBagsContext);

  const discount = order.discounts?.length > 0 ? [order.discounts[0].discount.code] : [];
  const existingOrderProducts = useOrderSummaryProductsForPayload(order.products);
  const [discountCodes] = useState<string[]>(discount);

  useEffect(() => {
    const fetchBufferBags = async () => {
      setBufferBagList(
        (await getBufferBags({ orderId: order.id })).filter(
          (bufferBag) => bufferBag.pet_id === selectedDog?.value
        )
      );
    };
    fetchBufferBags();
  }, [order.id, selectedDog?.value]);

  const updateBufferBag = (
    petBufferBag: BufferBag,
    bufferBagProduct: BufferBagProduct,
    quantity: number
  ) => {
    setSelectedBufferBags((prev) => ({
      ...prev,
      [`${petBufferBag.pet_plan_id}~${bufferBagProduct.buffer_bag_product_code}`]: quantity,
    }));
  };

  useEffect(() => {
    if (bufferBagList?.[0]) {
      const { totalDays, totalCost } = calculateTotalsPerDog(
        bufferBagList?.[0].pet_plan_id,
        bufferBagList,
        selectedBufferBags
      );
      setTotals({ totalDays, totalCost });
    }
  }, [bufferBagList, selectedBufferBags]);

  const handleSubmit = (isFreeProducts: boolean) => {
    const productUpdatePayload = prepareBufferBagProducts(
      selectedBufferBags,
      bufferBagList,
      isFreeProducts
    );

    const apiOrderProducts = [...productUpdatePayload, ...existingOrderProducts].map(
      (orderProduct: any) => {
        if (orderProduct.quantity !== 0) {
          return {
            pet_plan: orderProduct.pet_plan,
            product_code: orderProduct.product_code,
            quantity: orderProduct.quantity,
            type: orderProduct.type,
          };
        }
      }
    );
    axios
      .put(`orders/${order.id}`, {
        account: customer.id,
        order_products: apiOrderProducts,
        discount_codes: discountCodes,
        order_type: order.order_type,
        order_id: order.id,
      })
      .then(() => {
        setShowBufferBagsConfirmationModal(false);
        setShowFreeBufferBagsConfirmationModal(false);
        const toastMsg = `We added Buffer Bag product(s) to this upcoming order.`;
        toast.success(toastMsg, BOTTOM_TOAST);
        onConfirm();
      })
      .catch((e) => {
        toast.error(generateErrorMsg(e), BOTTOM_TOAST);
      });
  };

  return (
    <>
      <div className="bg-white mt-3 max-w-[1000px] w-[100%] max-h-[500px] overflow-scroll relative">
        <div className="font-bold text-left mb-2">Select extra meals for {selectedDog?.label}</div>
        <div className="pt-1 pb-5 flex-col items-center justify-between space-y-2">
          {bufferBagList?.[0]?.buffer_bag_products.map((bufferBagProduct) => {
            const bufferBagOptions = generateFormattedBufferOptions(
              bufferBagList[0].buffer_bag_extra_days,
              bufferBagList[0].buffer_bag_number_of_bags_allowed
            );
            return (
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-[70px] mr-[10px]">
                    <Dropdown
                      options={bufferBagOptions}
                      selectedOption={
                        bufferBagOptions.find(
                          (option) =>
                            option.value ===
                            String(
                              selectedBufferBags[
                                `${bufferBagList[0].pet_plan_id}~${bufferBagProduct.buffer_bag_product_code}`
                              ]
                            )
                        ) || bufferBagOptions[0]
                      }
                      setSelectedOption={(newSelection) =>
                        updateBufferBag(
                          bufferBagList[0],
                          bufferBagProduct,
                          Number(newSelection.value)
                        )
                      }
                    />
                  </div>
                  Extra days of {bufferBagProduct.recipe_name}
                </div>
                <span className="font-semibold">
                  {`$${(
                    Number(bufferBagProduct.buffer_bag_price) *
                      selectedBufferBags[
                        `${bufferBagList[0].pet_plan_id}~${bufferBagProduct.buffer_bag_product_code}`
                      ] || 0
                  ).toFixed(2)}`}{' '}
                  {order.currency}
                </span>
              </div>
            );
          })}
        </div>
        <hr />
        <div className="flex items-center justify-between">
          <div className="font-bold text-left mt-5">
            Total for {totals.totalDays} extra days of food
          </div>
          <span className="font-semibold">
            ${totals.totalCost.toFixed(2)} {order.currency}
          </span>
        </div>

        <div className="mt-[45px] w-full flex flex-col items-center space-y-2">
          <Button
            isDisabled={selectedDog === undefined}
            label="Add extra meals to order"
            variant={selectedDog ? 'primary' : 'secondary'}
            onClick={() => setShowBufferBagsConfirmationModal(true)}
          />
          <a
            onClick={() => setShowFreeBufferBagsConfirmationModal(true)}
            className="font-bold cursor-pointer underline"
          >
            Add extra meals to order FOR FREE
          </a>
        </div>
      </div>
      {showBufferBagsConfirmationModal && (
        <ConfirmationModal
          title="Are you sure you want to proceed?"
          message={`By clicking “Confirm” below, you will add this product on a one-time basis to this upcoming order scheduled for ${scheduledDate}`}
          confirmLabel="Confirm"
          onConfirm={() => handleSubmit(false)}
          isProcessLoading={false}
          onCancel={() => setShowBufferBagsConfirmationModal(false)}
        />
      )}
      {showFreeBufferBagsConfirmationModal && (
        <ConfirmationModal
          title="Are you sure you want to proceed?"
          message={`By clicking “Confirm” below, you will add this product on a one-time basis to this upcoming order scheduled for ${scheduledDate}`}
          customMessage={
            <p className="text-sm mb-4 text-center">
              The customer will <span style={{ fontWeight: 'bold' }}>not</span> be charged for these
              Extra meals.
            </p>
          }
          confirmLabel="Confirm"
          onConfirm={() => handleSubmit(true)}
          isProcessLoading={false}
          onCancel={() => setShowFreeBufferBagsConfirmationModal(false)}
        />
      )}
    </>
  );
};
